import { FC, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import Flatpickr from "react-flatpickr";
import {Vietnamese} from "flatpickr/dist/l10n/vn.js"
import { PaginateModel } from "../../../models/PaginateModel";
import { SaleRowModel, SaleRowSearchModel } from "../../../models/account-books/SalesAccount";
import { PaginationLength } from "../../../../_metronic/partials/pagination/PaginationLength";
import { Paginate } from "../../../../_metronic/partials/pagination/Paginate";
import { DEFAULT_PER_PAGE } from "../../../constants/common";
import ConfigModal from "../../../components/Modal/ConfigModal";
import { HeaderModel, ModalModel } from "../../../models/CommonModel";
import WarehousesAccountTable from "../components/WarehousesAccountTable";
import { WarehouseRowModel, WarehouseRowSearchModel } from "../../../models/account-books/WarehousesAccount";
import { exportWarehousesBooks, exportWarehousesList, getWarehousesBooks, refreshWarehousesBooks } from "../AccountBooksCRUD";
import { download, Toast } from "../../../modules/common/helper";
import { direction } from "html2canvas/dist/types/css/property-descriptors/direction";
import { formatPrice } from "../../../selectors";

interface Props {
    opening?: string
    closing?: string
    changeTab: (tab: string) => void
    isFullYear: boolean
}

const WarehousesAccount: FC<Props> = ({ opening, closing, changeTab, isFullYear }) => {
    const intl = useIntl();
    const fp = useRef<any>(null);
    const initSearch = useMemo(() => ({
        page: 1,
        per_page: DEFAULT_PER_PAGE,
        start_date: opening,
        end_date: closing,
        direction: '',
        invoice_status: ''
    }), [opening, closing])
    const [search, setSearch] = useState<WarehouseRowSearchModel>(initSearch)
    const [data, setData] = useState<PaginateModel<WarehouseRowModel>>()
    const [openingStockLine, setOpeningStockLine] = useState<WarehouseRowModel>()
    const [subTotal, setSubTotal] = useState<{total_export_amount: number, total_import_amount: number, total_cumulative_stock: number}>({total_export_amount: 0, total_import_amount: 0, total_cumulative_stock: 0});
    const [configModal, setConfigModal] = useState<ModalModel>({
        show: false,
        mode: 'add',
    });
    const [hideCols, setHideCols] = useState<string[]>(['status']);
    const header: HeaderModel[] = [
        {
            label: 'Ngày HĐ gốc',
            field: 'invoice_date',
            style: { maxWidth: "90px", minWidth: "90px", },
            sortable: true
        },
        {
            label: 'Ký hiệu HĐ',
            field: 'invoice_code',
            style: { minWidth: "70px" },
            sortable: true
        },
        // {
        //     label: 'Số hóa đơn',
        //     field: 'voucher_no',
        //     style: { maxWidth: "90px", minWidth: "90px", },
        //     sortable: true
        // },
        {
            label: 'Mã SP',
            field: 'product_code',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: true
        },
        {
            label: 'Tên SP',
            field: 'product_name',
            style: { maxWidth: "120px", minWidth: "120px", },
            sortable: true
        },
        {
            label: 'SL nhập',
            field: 'qty_in',
            style: { maxWidth: "70px", minWidth: "70px", },
            sortable: true
        },
        {
            label: 'SL xuất',
            field: 'qty_out',
            style: { maxWidth: "70px", minWidth: "70px", },
            sortable: true
        },
        {
            label: 'Tồn sau GD',
            field: 'cumulative_stock',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Giá vốn',
            field: 'unit_cost',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Giá bán',
            field: 'sell_price',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Tổng tiền',
            field: 'line_amount',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: true
        },
        {
            label: 'Diễn giải',
            field: 'type',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: false
        },
        {
            label: 'Trạng thái',
            field: 'status',
            style: { maxWidth: "100px", minWidth: "100px", },
            sortable: false
        }
    ]

    const handleChangeVisCols = (e: any) => {
        let hiddenCols = [...hideCols];
        if (!e.target?.checked) {
            setHideCols([...hiddenCols, e.target.name])
        } else {
            setHideCols(hiddenCols.filter((item) => item !== e.target.name))
        }
    }

    useEffect(() => {
        if (opening && closing) {
            getData({ ...initSearch });
        }
    }, [opening, closing, initSearch])

    const getData = async (searchParams: WarehouseRowSearchModel) => {
        try {
            const { data: resData } = await getWarehousesBooks(searchParams);
            setData(resData.paginate_data);
            setSubTotal(resData.sub_total);
            setOpeningStockLine(resData.opeing_stock_line)
            setSearch(searchParams)
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }
    const handleSearch = (e: any) => {
        e.preventDefault();
        getData({...search, page: 1})
    }

    const handleClearSearch = (e: any) => {
        if (fp.current?.flatpickr) {
            fp.current.flatpickr.setDate([
                moment(opening).format('DD-MM-YYYY'), 
                moment(closing).format('DD-MM-YYYY')
            ]);
        }
        getData({...initSearch})
    }

    const handlePageLengthChange = (e: any) => {
        getData({ ...search, page: 1, per_page: +e.target.value })
    }

    const handlePageChange = (data: any) => {
        getData({ ...search, page: data.selected + 1 })
    }

    const handleSort = (sortBy: string, direction: 'asc' | 'desc' | 'none') => {
        const currentSearch = { ...search };
        const sortParams: WarehouseRowSearchModel = { ...currentSearch, sort_by: sortBy, sort_direction: direction };
        if (direction === 'none') {
            delete sortParams.sort_by;
            delete sortParams.sort_direction;
        }
        getData({ ...sortParams });
    }

    const toggleByNegStock = (e: React.ChangeEvent<HTMLInputElement>) => {
        getData({
            ...search,
            only_negative_cumulative_stock: e.target.checked ? 1 : 0, 
            page: 1
        })
    }

    const toggleUnderCost = (e: React.ChangeEvent<HTMLInputElement>) => {
        getData({
            ...search,
            under_cost: e.target.checked ? 1 : 0, 
            page: 1
        })
    }

    const handleExport = async () => {
        const { data } = await exportWarehousesBooks({ ...search });
        download(data, '4-So kho');
    }
    
    const handleExportList = async () => {
        const { data } = await exportWarehousesList({ ...search });
        download(data, '4-So kho - Danh sach');
    }

    const handleRefreshWarehouseHub = async () => {
        try {
            const { data: resData } = await refreshWarehousesBooks({ ...search });
            setData(resData.paginate_data);
            setSubTotal(resData.sub_total);
            Toast.fire({
                icon: 'success',
                title: 'Dữ liệu đã được làm mới!'
            })
        } catch (e: any) {
            Toast.fire({
                icon: 'error',
                title: 'Đã có lỗi xảy ra, vui lòng thử lại sau!'
            })
        }
    }

    const totalCard = (title: string, amount: number, variant: string, icon: string) => {
        return (
            <div
                className="card shadow-sm border-0"
                style={{ maxWidth: 400, borderRadius: 12 }}
            >
                <div className="card-body d-flex align-items-center">

                    <div
                        className={`d-flex align-items-center justify-content-center rounded-circle bg-light-${variant}`}
                        style={{
                            width: 50,
                            height: 50,
                        }}
                    >
                        <i
                            className={`${icon} text-${variant}`}
                            style={{ fontSize: 24 }}
                        ></i>
                    </div>

                    <div className="ms-3">
                        <div className="text-dark fw-bold" style={{ fontSize: 14 }}>
                            {title}
                        </div>

                        <div
                            className={`fw-bold d-flex align-items-center`}
                            style={{ fontSize: 24 }}
                        >
                            <span className={`text-${variant}`}>{formatPrice(amount)}</span>
                            <span style={{ fontSize: 14, marginLeft: '5px'}}>VND</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const totalCumulativeStockCard = (title: string, amount: number, variant: string, icon: string) => {
        return (
            <div
                className="card shadow-sm border-0"
                style={{ maxWidth: 400, borderRadius: 12 }}
            >
                <div className="card-body d-flex align-items-center">

                    <div
                        className={`d-flex align-items-center justify-content-center rounded-circle bg-light-${variant}`}
                        style={{
                            width: 50,
                            height: 50,
                        }}
                    >
                        <i
                            className={`${icon} text-${variant}`}
                            style={{ fontSize: 24 }}
                        ></i>
                    </div>

                    <div className="ms-3">
                        <div className={`text-${variant} fw-bold`} style={{ fontSize: 14 }}>
                            {title} : {formatPrice(amount)}
                        </div>
                        {amount < 0 && (
                            <div
                                className={`d-flex align-items-center`}
                            >
                                <span className={`text-dark`}>Mã hàng đang xuất vượt số lượng nhập: {formatPrice(amount)}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return <>
        <div className='card mt-3'>
            <div className="card-header card-header-stretch p-3">
                <h3 className="card-title">
                    <button type="button" className="btn btn-primary btn-sm mx-1 mb-1 me-2" onClick={() => changeTab('index')}>
                        <i className="fas fa-angle-left" />
                    </button>
                    Sổ kho
                </h3>
                <div className="card-toolbar align-items-center">
                    <div className="btn-groups">
                        {!isFullYear && (
                            <button className="btn ms-2 btn-sm btn-primary text-hover-white" type="button"
                                onClick={handleExport}>
                                Tải xuống
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <div className="card-header p-3">
                <div className="col-md-12">
                    <form onSubmit={handleSearch}>
                        <div className="row">
                            <div className='col-md-3 mb-2'>
                                <label className="form-label">Tên sản phẩm</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Tên sản phẩm/Mã hàng"
                                    name="product_name"
                                    autoComplete='nope'
                                    value={search?.product_name ?? ''}
                                    onChange={(e) => setSearch({ ...search, product_name: e.target.value })}
                                />
                            </div>
                            <div className='col-md-3 mb-2'>
                                <label className="form-label">Khoảng thời gian</label>
                                <Flatpickr
                                    ref={fp}
                                    onChange={(date) => {
                                        let startDate = moment(date[0]).format('YYYY-MM-DD');
                                        let endDate = moment(date[1]).format('YYYY-MM-DD');
                                        setSearch({ ...search, start_date: startDate, end_date: endDate });
                                    }}
                                    options={{
                                        mode: "range",
                                        dateFormat: "d-m-Y",
                                        locale: Vietnamese,
                                        shorthandCurrentMonth: true,
                                        minDate: moment(opening).format('DD-MM-YYYY'),
                                        maxDate: moment(closing).format('DD-MM-YYYY'),
                                        defaultDate: [moment(opening).format('DD-MM-YYYY'), moment(closing).format('DD-MM-YYYY')],
                                    }}
                                    className='form-control'
                                    placeholder=''
                                />
                            </div>
                            <div className="col-md-3 mb-2">
                                <label className="form-label">Nhập/Xuất</label>
                                <select
                                    className="form-select"
                                    value={search.direction}
                                    onChange={(e) => setSearch({ ...search, direction: e.target.value })}
                                    name="direction"
                                >
                                    <option value="">Tất cả</option>
                                    <option value="in">Nhập</option>
                                    <option value="out">Xuất</option>
                                </select>
                            </div>
                            <div className="col-md-3 mb-2">
                                <label className="form-label">Trạng thái chứng từ</label>
                                <select
                                    className="form-select"
                                    value={search.invoice_status}
                                    onChange={(e) => setSearch({ ...search, invoice_status: parseInt(e.target.value) })}
                                    name="invoice_status"
                                >
                                    <option value="">Tất cả</option>
                                    <option value="1">Có</option>
                                    <option value="0">Không</option>
                                </select>
                            </div>
                            <div className='col-md-3 mb-2'>
                                <label className="form-label">Thông tin hóa đơn</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder=""
                                    name="invoice_code"
                                    autoComplete='nope'
                                    value={search?.invoice_code ?? ''}
                                    onChange={(e) => setSearch({ ...search, invoice_code: e.target.value })}
                                />
                            </div>
                            <div className='col-md-3 mb-2'>
                                <label className="form-label invisible">Chỉ hiển thị âm kho</label>
                                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                    <input 
                                        className='form-check-input' 
                                        type='checkbox' 
                                        name='has_invoice_code' 
                                        checked={search?.only_negative_cumulative_stock == 1} 
                                        onChange={toggleByNegStock} />
                                    <span className='form-check-label'>Chỉ hiển thị âm kho</span>
                                </label>
                            </div>
                            <div className='col-md-3 mb-2'>
                                <label className="form-label invisible">Bán dưới giá vốn</label>
                                <label className='form-check form-check-sm form-check-custom form-check-solid me-5'>
                                    <input 
                                        className='form-check-input' 
                                        type='checkbox' 
                                        name='under_cost' 
                                        checked={search?.under_cost == 1}  
                                        onChange={toggleUnderCost} />
                                    <span className='form-check-label'>Bán dưới giá vốn</span>
                                </label>
                            </div>
                        </div>
                        <div className="row">
                            <div className="btns-group">
                            <button
                                className="col-auto btn btn-primary mt-2 mx-1"
                                onClick={handleSearch}
                            >
                                <i className="bi bi bi-search fs-4"></i> {intl.formatMessage({ id: 'COMMON.SEARCH' })}
                            </button>
                            <button
                                className="col-auto btn btn-danger mt-2 mx-1"
                                type="button"
                                onClick={handleClearSearch}
                            >
                                <i className="bi bi-eraser-fill fs-1"></i> Re-search
                            </button>
                            <button className="col-auto btn btn-sm btn-icon mt-2  mx-1 btn-light-info text-hover-white" type="button"
                                onClick={handleExportList}>
                                <i className="bi bi bi-cloud-arrow-down-fill fs-1 text-info"></i>
                            </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <div className='card-body p-3'>
                <div className="align-items-center my-3 row">
                    <div className='col-12 col-md-4'>
                        {totalCard(
                            'Tổng tiền nhập', 
                            subTotal.total_import_amount, 
                            'primary', 
                            'bi bi-cart'
                        )}
                    </div>
                    <div className='col-12 col-md-4'>
                        {totalCard(
                            'Tổng tiền xuất', 
                            subTotal.total_export_amount, 
                            'success', 
                            'bi bi-box-arrow-up'
                        )}
                    </div>
                    <div className='col-12 col-md-4'>
                        {totalCumulativeStockCard(
                            'Tồn kho hiện tại', 
                            subTotal.total_cumulative_stock, 
                            subTotal.total_cumulative_stock < 0 ? 'danger' : 'success', 
                            'fa fa-exclamation'
                        )}
                    </div>
                </div>
                <WarehousesAccountTable
                    data={data?.data || []}
                    openColConfig={() => setConfigModal({ ...configModal, show: true })}
                    header={header}
                    hideCols={hideCols}
                    handleSort={handleSort}
                    openingStockLine={openingStockLine}
                />
            </div>
            <div className='card-footer d-flex justify-content-between paginate-wrapper'>
                <PaginationLength selected={search.per_page || DEFAULT_PER_PAGE} onChange={handlePageLengthChange} />
                <Paginate
                    pageCount={data?.meta?.last_page || 0}
                    onPageChange={handlePageChange}
                    forcePage={(search.page || 1) - 1}
                />
            </div>
            <ConfigModal
                {...configModal}
                header={header}
                hideCols={hideCols}
                handleChangeVisCols={handleChangeVisCols}
                onClose={() => { setConfigModal({ ...configModal, show: false }) }}
            />
        </div>
    </>
}

export default WarehousesAccount;